import { URLParamKey } from "constants/urlParams"
import Languages from "services/i18n/languages"
import { getParamCaseInsensitive } from "utils/urlParams.util"

export const LANGUAGE_STORAGE_KEY = "language"
export const DEFAULT_LANGUAGE = "he"
export const URL_SUPPORTED_LANGUAGES = ["he", "ar"] as const

type UrlSupportedLanguage = (typeof URL_SUPPORTED_LANGUAGES)[number]

const isKnownAppLanguage = (lang: string): boolean =>
  Languages.some((language) => language.key === lang)

const isUrlSupportedLanguage = (lang: string): lang is UrlSupportedLanguage =>
  (URL_SUPPORTED_LANGUAGES as ReadonlyArray<string>).includes(lang)

/**
 * Normalizes a URL lang param to a supported locale key (he/ar), or null if unsupported.
 * Accepts region variants such as he-IL / ar_IL (case-insensitive).
 */
export const normalizeLangParam = (raw: string | null): UrlSupportedLanguage | null => {
  if (!raw) {
    return null
  }

  const normalized = raw.trim().toLowerCase().split(/[-_]/)[0]

  if (!normalized || !isUrlSupportedLanguage(normalized)) {
    return null
  }

  return normalized
}

export const getDirectionForLanguage = (key: string): "rtl" | "ltr" =>
  Languages.find((language) => language.key === key)?.isRtl ? "rtl" : "ltr"

/**
 * Removes the `lang` query param (case-insensitive) from a URL search string.
 */
export const removeLangParamFromSearch = (search: string): string => {
  const queryParams = new URLSearchParams(search)
  const keysToDelete: Array<string> = []

  for (const key of queryParams.keys()) {
    if (key.toLowerCase() === URLParamKey.LANG.toLowerCase()) {
      keysToDelete.push(key)
    }
  }

  if (keysToDelete.length === 0) {
    return search
  }

  keysToDelete.forEach((key) => {
    queryParams.delete(key)
  })

  const serialized = queryParams.toString()
  return serialized ? `?${serialized}` : ""
}

/**
 * Resolves the initial app language:
 * 1. Prefer a valid ?lang= URL param (host-driven).
 * 2. Otherwise use a known stored preference (in-iframe persistence).
 * 3. Fallback to Hebrew.
 */
export const resolveInitialLanguage = (
  search: string = typeof window !== "undefined" ? window.location.search : ""
): string => {
  const queryParams = new URLSearchParams(search)
  const langParam = getParamCaseInsensitive(queryParams, URLParamKey.LANG)
  const urlLanguage = normalizeLangParam(langParam)

  if (urlLanguage) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, urlLanguage)
    return urlLanguage
  }

  const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (storedLanguage && isKnownAppLanguage(storedLanguage)) {
    return storedLanguage
  }

  localStorage.setItem(LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE)
  return DEFAULT_LANGUAGE
}
